import { useCallback, useEffect, useState } from 'react';

const CONSENT_BANNER_ELEMENT_ID = 'consent-banner';
const CONSENT_CHANGED_EVENT = 'WcpConsentChanged';

const getIsConsentRequired = () =>
  Boolean(window.siteConsent && window.siteConsent.isConsentRequired);

const notifyConsentChanged = () => {
  window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT));
};

/**
 * Initializes the WCP (Web Cookie Policy) cookie consent banner.
 *
 * Relies on the wcp-consent.js library being loaded via index.html and on the
 * `#consent-banner` placeholder element existing in the DOM. Safe to call more
 * than once; initialization only happens on the first successful call.
 *
 * @param {Object} [options] Initialization options.
 * @param {string} [options.locale] Culture string (e.g. 'en-us'). Falls back to
 *   the document/browser language when not provided.
 * @param {Object} [options.logger] Optional logger exposing an `error` method.
 * @returns {void}
 */
export const initWcpConsent = ({ locale, logger } = {}) => {
  if (!window.WcpConsent || window.siteConsent) {
    return;
  }

  const culture = locale || document.documentElement.lang || navigator.language || 'en-us';

  // WCP Consent API: https://eng.ms/docs/cloud-ai-platform/devdiv/one-engineering-system-1es/1es-docs/wcp/privacy/wcp-cookie-consent-api
  window.WcpConsent.init(
    culture,
    CONSENT_BANNER_ELEMENT_ID,
    (err, siteConsent) => {
      if (err) {
        if (logger && typeof logger.error === 'function') {
          logger.error(`[WcpConsent] init failed: ${err}`);
        }
        return;
      }

      window.siteConsent = siteConsent;
      notifyConsentChanged();
    },
    notifyConsentChanged,
    window.WcpConsent.themes.light
  );
};

/**
 * React hook exposing the current WCP consent state.
 *
 * @returns {Object} An object with `isConsentRequired` (boolean; true in regions
 *   where consent management is required, e.g. EU/EEA) and `manageConsent`
 *   (function that re-opens the cookie preferences dialog).
 */
export const useWcpConsent = () => {
  const [isConsentRequired, setIsConsentRequired] = useState(getIsConsentRequired);

  useEffect(() => {
    const handleConsentChanged = () => setIsConsentRequired(getIsConsentRequired());

    // Sync in case consent was initialized before this hook mounted.
    handleConsentChanged();
    window.addEventListener(CONSENT_CHANGED_EVENT, handleConsentChanged);

    return () => window.removeEventListener(CONSENT_CHANGED_EVENT, handleConsentChanged);
  }, []);

  const manageConsent = useCallback(() => {
    if (window.siteConsent && window.siteConsent.isConsentRequired) {
      window.siteConsent.manageConsent();
    }
  }, []);

  return { isConsentRequired, manageConsent };
};
